import type { CheckoutEntitlementGrant } from "./checkout-contract";
import type { PaidFeatureId } from "./paid-features";
import { PURCHASE_ACCESS_EVIDENCE_SCHEMA } from "./purchase-access-evidence";
import { isSafePurchaseInstallId } from "./purchase-installation-id";
import type { PurchaseVerificationConfigResult } from "./purchase-verification-client";

export interface PurchaseAccessEvidenceConfig {
  endpoint: string;
  metadataPolicy: "no_document_contents";
}

export type PurchaseAccessEvidenceConfigResult =
  | { type: "configured"; config: PurchaseAccessEvidenceConfig }
  | { type: "reject"; reason: "not_configured" | "unsupported_restore_endpoint" };

export type BrowserPurchaseAccessEvidenceKind = "paid_tool_accessed" | "download_accessed";

export interface BrowserPurchaseAccessEvidenceRequest {
  kind: BrowserPurchaseAccessEvidenceKind;
  featureId?: PaidFeatureId;
}

interface BrowserPurchaseAccessEvidenceBody {
  schema: typeof PURCHASE_ACCESS_EVIDENCE_SCHEMA;
  metadataPolicy: "no_document_contents";
  kind: BrowserPurchaseAccessEvidenceKind;
  provider: string;
  providerPurchaseId: string;
  plan: CheckoutEntitlementGrant["plan"];
  installId: string;
  featureId?: PaidFeatureId;
}

export type PurchaseAccessEvidenceRecordResult =
  | { ok: true }
  | {
      ok: false;
      reason:
        | "not_configured"
        | "invalid_grant"
        | "invalid_kind"
        | "invalid_response"
        | "request_failed"
        | "rejected";
    };

const RESTORE_PATH_SUFFIX = "/v1/invoice-web/entitlements/restore";
const ACCESS_EVENTS_PATH = "/v1/invoice-web/access-events";
const SAME_ORIGIN_RESTORE_PATH = "/api/purchase-restore";
const SAME_ORIGIN_ACCESS_EVENTS_PATH = "/api/purchase-access-events";
const OWNED_PURCHASE_VERIFIER_ORIGIN = "https://polenest-entitlements.itseoulite.workers.dev";
const REDACTED_PROVIDER_PURCHASE_REFERENCE_PATTERN = /^(?:sha256:)?[a-f0-9]{32,128}$/i;

export function purchaseAccessEvidenceConfigFromVerificationConfig(
  verificationConfig: PurchaseVerificationConfigResult,
): PurchaseAccessEvidenceConfigResult {
  if (verificationConfig.type !== "configured") {
    return { type: "reject", reason: "not_configured" };
  }

  const endpoint = accessEventsEndpointFromRestoreEndpoint(verificationConfig.config.endpoint);
  if (!endpoint) return { type: "reject", reason: "unsupported_restore_endpoint" };

  return {
    type: "configured",
    config: {
      endpoint,
      metadataPolicy: "no_document_contents",
    },
  };
}

export function accessEventsEndpointFromRestoreEndpoint(endpoint: string): string | null {
  if (endpoint === SAME_ORIGIN_RESTORE_PATH) return SAME_ORIGIN_ACCESS_EVENTS_PATH;

  try {
    const url = new URL(endpoint);
    if (
      url.protocol !== "https:" ||
      url.origin !== OWNED_PURCHASE_VERIFIER_ORIGIN ||
      url.pathname !== RESTORE_PATH_SUFFIX ||
      url.search ||
      url.hash ||
      url.username ||
      url.password
    ) {
      return null;
    }
    url.pathname = ACCESS_EVENTS_PATH;
    url.search = "";
    url.hash = "";
    return url.toString();
  } catch {
    return null;
  }
}

export async function recordPurchaseAccessEvidence({
  config,
  grant,
  installId,
  kind,
  featureId,
  fetcher = fetch,
}: {
  config: PurchaseAccessEvidenceConfigResult;
  grant: CheckoutEntitlementGrant | null;
  installId: string | null;
  kind: BrowserPurchaseAccessEvidenceKind;
  featureId?: PaidFeatureId;
  fetcher?: typeof fetch;
}): Promise<PurchaseAccessEvidenceRecordResult> {
  if (config.type !== "configured") return { ok: false, reason: "not_configured" };
  if (!isSafeBrowserAccessEvidenceKind(kind)) return { ok: false, reason: "invalid_kind" };
  if (!isSafeAccessEvidenceGrant(grant) || !installId || !isSafePurchaseInstallId(installId)) {
    return { ok: false, reason: "invalid_grant" };
  }
  if (featureId && !grant.features.includes(featureId)) {
    return { ok: false, reason: "invalid_grant" };
  }

  const body: BrowserPurchaseAccessEvidenceBody = {
    schema: PURCHASE_ACCESS_EVIDENCE_SCHEMA,
    metadataPolicy: config.config.metadataPolicy,
    kind,
    provider: grant.provider,
    providerPurchaseId: grant.providerPurchaseId,
    plan: grant.plan,
    installId,
    ...(featureId ? { featureId } : {}),
  };

  try {
    const response = await fetcher(config.config.endpoint, {
      method: "POST",
      credentials: config.config.endpoint.startsWith("/") ? "same-origin" : "omit",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) return { ok: false, reason: "rejected" };
    const payload = await response.json().catch(() => null);
    return isSafeAccessEvidenceResponse(payload, body)
      ? { ok: true }
      : { ok: false, reason: "invalid_response" };
  } catch {
    return { ok: false, reason: "request_failed" };
  }
}

function isSafeAccessEvidenceGrant(
  grant: CheckoutEntitlementGrant | null,
): grant is CheckoutEntitlementGrant {
  return (
    !!grant &&
    grant.source === "verified_checkout" &&
    typeof grant.provider === "string" &&
    grant.provider.length > 0 &&
    isRedactedProviderPurchaseReference(grant.providerPurchaseId)
  );
}

function isSafeBrowserAccessEvidenceKind(
  value: unknown,
): value is BrowserPurchaseAccessEvidenceKind {
  return value === "paid_tool_accessed" || value === "download_accessed";
}

function isSafeAccessEvidenceResponse(
  value: unknown,
  requestBody: BrowserPurchaseAccessEvidenceBody,
): boolean {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const payload = value as Record<string, unknown>;
  return (
    payload.ok === true &&
    payload.schema === PURCHASE_ACCESS_EVIDENCE_SCHEMA &&
    payload.metadataPolicy === "no_document_contents" &&
    payload.provider === requestBody.provider &&
    payload.providerPurchaseId === requestBody.providerPurchaseId &&
    payload.kind === requestBody.kind &&
    payload.plan === requestBody.plan &&
    typeof payload.occurredAt === "string" &&
    !Number.isNaN(Date.parse(payload.occurredAt)) &&
    payload.stored === true &&
    isRedactedProviderPurchaseReference(String(payload.eventId ?? ""))
  );
}

function isRedactedProviderPurchaseReference(value: string): boolean {
  return REDACTED_PROVIDER_PURCHASE_REFERENCE_PATTERN.test(value);
}
